import React, { CSSProperties, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { useTableId, useTableSelector } from 'hooks/useTableSelector';
import { clearSeatJoinError } from 'reducers/currentTable';
import { selectIsQueuedFor } from 'reducers/waitlist.selectors';
import { describeSeatJoinError } from 'utils/seatJoinErrorCopy';

import GameModal, { btnRowStyle, primaryBtnStyle } from 'components/GameModal/GameModal';

/**
 * A seat-request rejection, shown on the table it happened at.
 *
 * Before: every `gotJoinGame` error went to the app-level ErrorMsgModal, which
 * sits outside any table cell — so in multi-table view a "Seat taken!" on one
 * table blurred and blocked EVERY open table, with the engine's raw wording
 * ("Table seating error: Invalid Seat #1!") and nothing to say which felt it
 * was about.
 *
 * Now: App.tsx routes the rejection onto the affected table's own slice
 * (`seatJoinError`, dispatched with meta.gameID — see services/pendingSeatJoin
 * for how the table is identified when the engine's frame omits it), and this
 * component renders it from inside that table's GameInner. GameModal does the
 * rest: in grid mode it portals into the cell root, so the backdrop covers only
 * this table; in single-table view it centres over the one felt on screen.
 * The copy comes from utils/seatJoinErrorCopy so the player reads what happened
 * and what to do, never the engine's log text.
 */
const SeatJoinErrorModal = () => {
  const dispatch = useAppDispatch();
  const gameID = useTableId();
  const error = useTableSelector((table) => table.seatJoinError);
  const isSeated = useTableSelector((table) => table.currentPlayer.isSeated);
  const isQueued = useAppSelector(selectIsQueuedFor(Number(gameID)));

  const dismiss = () => {
    dispatch({ ...clearSeatJoinError(), meta: { gameID } });
  };

  // The engine seated us after all (a retry landed, or a later broadcast shows
  // us in the seat) — a stale "seat taken" would now be contradicting the felt.
  useEffect(() => {
    if (error && isSeated) dismiss();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, isSeated]);

  const copy = describeSeatJoinError(error?.message, { isQueued });

  return (
    <GameModal
      open={!!error}
      onClose={dismiss}
      title={copy.title}
      // Centred for every variant of this modal, not just "Seat no longer
      // available": the body copy and the single action button are already
      // centred, and a left-aligned title on top of them read as a stray
      // header. Opt-in per modal (GameModal defaults to left), so no other
      // dialog is restyled.
      titleAlign='center'
      // Tapping outside dismisses (EN-10789). `dismiss` only clears this
      // table's seatJoinError slice — the same thing the single action button
      // does — so an outside tap costs the player nothing.
      showClose={false}>
      <div style={messageStyle}>{copy.body}</div>
      <div style={btnRowStyle}>
        <button onClick={dismiss} style={primaryBtnStyle}>
          {copy.action}
        </button>
      </div>
    </GameModal>
  );
};

export default SeatJoinErrorModal;

const messageStyle: CSSProperties = {
  fontSize: '0.95rem',
  color: 'rgba(255, 255, 255, 0.7)',
  lineHeight: 1.5,
  textAlign: 'center',
  marginBottom: '0.5rem'
};
